import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useHotkeys } from 'react-hotkeys-hook'
import { toast } from 'sonner'
import { ArrowUpRight, Bell, BellOff, Check, FileText } from 'lucide-react'
import { useEntityWatch } from '../hooks/useEntityWatch'
import { hasConversation } from '../utils/notificationInbox'
import { NotificationConversation } from './NotificationConversation'
import type { NotificationDetailsPanelProps } from '../types'
import type { NotificationSubject } from '../hooks/useNotificationLookups'
import { CommentEditor } from '@/components/features/comments/CommentEditor'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { cn } from '@/lib/utils'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { getPriorityLevel } from '@/lib/utils/priority'
import { projectAccentColor } from '@/lib/utils/projectIcon'
import useLoginStore from '@/store/LoginStore'

const chipClass =
  'inline-flex h-6 items-center gap-1.5 rounded-md bg-secondary px-2 text-[12px] font-medium text-secondary-foreground'

/**
 * Right pane of the inbox (design 17a): the task (or doc) header, the
 * conversation with the triggering comment highlighted, and a reply composer.
 * Reply (R), react, Done (E) and Unfollow all work without opening the task.
 */
export function NotificationDetailsPanel({
  notification,
  notifications,
  lookups,
  onDone,
  showHints = true,
}: NotificationDetailsPanelProps) {
  const { t } = useTranslation('notifications')
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const composerRef = useRef<HTMLDivElement>(null)

  const subject = notification ? lookups.resolveSubject(notification) : null
  const watchEntityType =
    subject?.kind === 'task' ? 'work_item' : subject?.kind === 'doc' ? 'document' : null
  const watchEntityId = subject?.task?.id ?? subject?.page?.id ?? null
  const watch = useEntityWatch(watchEntityType, watchEntityId)

  const commentEntityType =
    subject?.kind === 'task' ? 'work_item' : subject?.kind === 'doc' ? 'page' : null
  const commentEntityId = watchEntityId
  const showConversation =
    !!notification && hasConversation(notification.type) && !!commentEntityType && !!commentEntityId

  useHotkeys(
    'r',
    (event) => {
      const editable = composerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')
      if (!editable) return
      event.preventDefault()
      editable.focus()
    },
    { enabled: showConversation },
    [showConversation],
  )

  if (!notification || !subject) return null

  const handleToggleFollow = async () => {
    try {
      if (watch.isFollowing) {
        await watch.unfollow()
        toast(t('toast.unfollowed'))
      } else {
        await watch.follow()
        toast(t('toast.followed'))
      }
    } catch {
      toast.error(t('toast.failed'))
    }
  }

  const actorName = lookups.actorLabel(notification.createdBy)
  const isOwnAction = notification.createdBy === lookups.currentUserId

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="notification-pane">
      <header className="flex shrink-0 items-center gap-3 border-b px-5 py-2.5">
        <Breadcrumb subject={subject} />
        <div className="ml-auto flex items-center gap-1.5">
          {watchEntityId && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-[12.5px]"
              onClick={handleToggleFollow}
              disabled={watch.isLoading || watch.isPending}
            >
              {watch.isFollowing ? <BellOff className="size-3.5" /> : <Bell className="size-3.5" />}
              {watch.isFollowing ? t('pane.unfollow') : t('pane.follow')}
            </Button>
          )}
          <Button
            size="sm"
            className="h-7 gap-1.5 px-2.5 text-[12.5px]"
            onClick={() => onDone(notifications && notifications.length > 0 ? notifications : [notification])}
            aria-keyshortcuts="E"
          >
            <Check className="size-3.5" />
            {t('pane.done')}
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5">
        <SubjectHeader subject={subject} lookups={lookups} />

        {!showConversation && notification.message && (
          <p className="mt-4 whitespace-pre-wrap rounded-lg bg-secondary px-3.5 py-3 text-[13.5px] leading-relaxed text-foreground">
            {notification.message}
          </p>
        )}

        {showConversation && (
          <section className="mt-5 border-t pt-4" aria-labelledby="notification-conversation-title">
            <h3
              id="notification-conversation-title"
              className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
            >
              {t('pane.conversation')}
            </h3>
            <NotificationConversation
              entityType={commentEntityType}
              entityId={commentEntityId}
              envId={workspaceId}
              notification={notification}
            />
          </section>
        )}
      </div>

      {showConversation && (
        <div ref={composerRef} className="shrink-0 px-5 pb-4 pt-2">
          <CommentEditor
            key={`${commentEntityType}:${commentEntityId}`}
            entityType={commentEntityType}
            entityId={commentEntityId}
            placeholder={
              isOwnAction ? t('pane.replyPlaceholder') : t('pane.replyTo', { name: actorName })
            }
            submitLabel={t('pane.reply')}
            trailing={
              showHints ? (
                <Kbd className="mb-1 hidden border bg-card sm:inline-flex">R</Kbd>
              ) : undefined
            }
          />
        </div>
      )}
    </div>
  )
}

function Breadcrumb({ subject }: { subject: NotificationSubject }) {
  const { t } = useTranslation('notifications')
  const space = subject.space
  return (
    <nav className="flex min-w-0 items-center gap-2 text-[13px]" aria-label={t('pane.breadcrumb')}>
      {space && (
        <>
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: projectAccentColor(space) }}
          />
          <span className="truncate text-foreground/80">{space.name}</span>
          <span className="text-muted-foreground">/</span>
        </>
      )}
      {subject.identifier && (
        <span className="shrink-0 font-mono text-[12px] font-medium uppercase text-foreground">
          {subject.identifier}
        </span>
      )}
    </nav>
  )
}

function SubjectHeader({
  subject,
  lookups,
}: {
  subject: NotificationSubject
  lookups: NotificationDetailsPanelProps['lookups']
}) {
  const { t } = useTranslation('notifications')
  const { t: tTasks } = useTranslation('tasks')
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)

  if (subject.kind === 'task' && subject.task) {
    const task = subject.task
    const status = lookups.statusById.get(task.statusId)
    const level = getPriorityLevel(task.priority ?? 0)
    const assigneeId = task.userId ?? null
    const assignee = assigneeId ? lookups.memberByUserId.get(assigneeId) : undefined
    const assigneeLabel =
      assigneeId && assigneeId === lookups.currentUserId
        ? t('pane.me')
        : assignee?.name || assignee?.email || null

    return (
      <div>
        <h2 className="flex items-start gap-2.5 text-[20px] font-semibold leading-snug tracking-[-0.01em]">
          <StatusIcon progressType={status?.progressType ?? 'BL'} statusId={status?.id} className="mt-1.5 shrink-0" />
          <span className="min-w-0">{task.name}</span>
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {status && <span className={chipClass}>{status.name}</span>}
          {level !== 'none' && (
            <span
              className={cn(
                chipClass,
                (level === 'high' || level === 'urgent') &&
                  'bg-brand-tint text-brand-tint-foreground',
              )}
            >
              {tTasks(`priority.${level}`)}
            </span>
          )}
          {assigneeLabel && (
            <span className={chipClass}>
              <Avatar className="size-4">
                <AvatarImage src={memberAvatarSrc(assignee)} alt="" />
                <AvatarFallback className="bg-muted-foreground/30 text-[8px]">
                  {assigneeLabel.slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {assigneeLabel}
            </span>
          )}
          <Link
            to="/$workspaceSlug/tasks/$taskId"
            params={{ workspaceSlug, taskId: task.id }}
            className={openLinkClass}
          >
            {t('pane.openTask')}
            <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
      </div>
    )
  }

  if (subject.kind === 'doc') {
    const page = subject.page
    const space = subject.space
    const project = subject.project
    const docLink =
      page && space ? (
        project && project.id !== space.id ? (
          <Link
            to="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug"
            params={{
              workspaceSlug,
              spaceKey: space.projectKey.toLowerCase(),
              projectKey: project.projectKey.toLowerCase(),
              pageSlug: page.slug,
            }}
            className={openLinkClass}
          >
            {t('pane.openDoc')}
            <ArrowUpRight className="size-3.5" />
          </Link>
        ) : (
          <Link
            to="/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug"
            params={{ workspaceSlug, spaceKey: space.projectKey.toLowerCase(), pageSlug: page.slug }}
            className={openLinkClass}
          >
            {t('pane.openDoc')}
            <ArrowUpRight className="size-3.5" />
          </Link>
        )
      ) : null
    return (
      <div>
        <h2 className="flex items-start gap-2.5 text-[20px] font-semibold leading-snug tracking-[-0.01em]">
          <FileText className="mt-1.5 size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0">{subject.title}</span>
        </h2>
        {docLink && <div className="mt-3 flex flex-wrap items-center gap-1.5">{docLink}</div>}
      </div>
    )
  }

  return (
    <h2 className="text-[20px] font-semibold leading-snug tracking-[-0.01em]">{subject.title}</h2>
  )
}

const openLinkClass =
  'inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[12px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground'
